import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Copy, ExternalLink, Link2, MapPin, Building2,
    Receipt, Shield, CreditCard, RotateCcw, Phone, BadgeCheck,
    UserPlus, Users, Mail, Percent,
} from "lucide-react";
import { toast } from "sonner";
import { bookingUrl as buildBookingUrl } from "@/lib/site";
import { primaryOutletIndex } from "@/lib/outlet";
import { ROLE_LABELS } from "@/lib/permissions";
import { needsStaffProfile, rosterRoleForLogin } from "@/lib/teamRoles";
import { DEFAULT_PAYROLL } from "@/lib/payroll";
import { Link } from "react-router-dom";

const EYEBROW = "font-eyebrow text-[11px] font-semibold uppercase tracking-[0.18em] sm:text-xs";

const GOLD = "#C9A227";
const GOLD_SOFT = "#D4AF37";
const CHOCOLATE = "#3D2817";
const MAROON = "#4A0E17";

const DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const DAY_LABELS = {
    mon: "Monday", tue: "Tuesday", wed: "Wednesday", thu: "Thursday",
    fri: "Friday", sat: "Saturday", sun: "Sunday",
};

const DEFAULT_HOURS = Object.fromEntries(
    DAY_KEYS.map((d) => [d, { open: "10:00", close: "20:00", closed: d === "sun" }]),
);

const navBtn =
    "border-[#E8E4DE] bg-white text-[#3D2817] shadow-sm hover:border-[#D4AF37]/45 hover:bg-[#FFFEF9]";

const fieldClass =
    "border-[#E8E4DE] bg-[#FFFEF9] focus-visible:border-[#D4AF37]/55 focus-visible:ring-[#D4AF37]/20";

const primaryBtn =
    "bg-[#4A0E17] font-semibold shadow-[0_4px_14px_rgba(74,14,23,0.25)] hover:bg-[#3a0b12]";

const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;

function clone(obj) {
    return JSON.parse(JSON.stringify(obj));
}

function normalizeHours(wh) {
    return { ...DEFAULT_HOURS, ...(wh || {}) };
}

function normalizeOutlet(o) {
    return {
        ...o,
        name: o.name || "",
        address: o.address || "",
        city: o.city || "",
        phone: o.phone || "",
        gstin: (o.gstin || "").toUpperCase(),
        working_hours: normalizeHours(o.working_hours),
    };
}

function validGstin(v) {
    const raw = (v || "").trim();
    return !raw || GSTIN_RE.test(raw);
}

function validPhone(v) {
    const raw = (v || "").trim();
    if (!raw) return true;
    const digits = raw.replace(/\D/g, "");
    return digits.length >= 10 && digits.length <= 13;
}

function GoldDivider({ className = "max-w-[200px]" }) {
    return (
        <div className={`flex items-center ${className}`}>
            <div className="h-px flex-1" style={{ background: `linear-gradient(90deg, transparent, ${GOLD_SOFT}B3, transparent)` }} />
            <span className="px-2.5 text-[10px] leading-none" style={{ color: GOLD }}>◆</span>
            <div className="h-px flex-1" style={{ background: `linear-gradient(90deg, transparent, ${GOLD_SOFT}B3, transparent)` }} />
        </div>
    );
}

function SalonCard({ children, className = "" }) {
    return (
        <section className={`relative overflow-hidden rounded-xl border border-[#E8E4DE] bg-white shadow-[0_4px_22px_rgba(45,41,38,0.04)] ${className}`}>
            <div className="absolute inset-x-0 top-0 h-[2px] bg-[#4A0E17]/85" />
            {children}
        </section>
    );
}

function SectionHead({ icon: Icon, title, subtitle, iconBg, iconColor, action }) {
    return (
        <div className="flex items-start justify-between gap-3 border-b border-[#E8E4DE]/80 pb-4">
            <div className="flex items-start gap-3">
                <div
                    className="flex size-10 shrink-0 items-center justify-center rounded-lg"
                    style={{ background: iconBg }}
                >
                    <Icon className="size-5" style={{ color: iconColor }} />
                </div>
                <div>
                    <h2 className="text-lg font-bold" style={{ color: CHOCOLATE }}>{title}</h2>
                    {subtitle && <p className="mt-1 text-sm font-medium text-[#8A8178]">{subtitle}</p>}
                </div>
            </div>
            {action}
        </div>
    );
}

function FormField({ label, hint, error, children, className = "" }) {
    return (
        <div className={`space-y-2 ${className}`}>
            <Label className="text-xs font-bold uppercase tracking-wide" style={{ color: CHOCOLATE }}>
                {label}
            </Label>
            {children}
            {error && <p className="text-[11px] font-semibold text-[#B8859C]">{error}</p>}
            {hint && !error && <p className="text-[11px] font-medium text-[#8A8178]">{hint}</p>}
        </div>
    );
}

function SaveBar({ dirty, saving, onSave, onDiscard, saveLabel, disabled }) {
    if (!dirty) return null;
    return (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-[#D4AF37]/35 bg-[#FDF3DC]/45 px-4 py-3">
            <BadgeCheck className="size-4 shrink-0 text-[#9A7B10]" />
            <span className="text-xs font-semibold text-[#9A7B10]">Unsaved changes</span>
            <div className="ml-auto flex flex-wrap gap-2">
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className={navBtn}
                    disabled={saving}
                    onClick={onDiscard}
                >
                    <RotateCcw className="mr-1.5 size-3.5" /> Discard
                </Button>
                <Button
                    type="button"
                    size="sm"
                    className={primaryBtn}
                    disabled={disabled || saving}
                    onClick={onSave}
                >
                    {saving ? "Saving…" : saveLabel}
                </Button>
            </div>
        </div>
    );
}

function WorkingHoursEditor({ value, onChange, disabled }) {
    const hours = normalizeHours(value);

    const patchDay = (day, patch) => {
        onChange({ ...hours, [day]: { ...hours[day], ...patch } });
    };

    const copyMondayToWeekdays = () => {
        const mon = hours.mon;
        const next = { ...hours };
        ["tue", "wed", "thu", "fri", "sat"].forEach((d) => {
            next[d] = { ...mon };
        });
        onChange(next);
        toast.success("Monday hours applied to Tue–Sat");
    };

    return (
        <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <Label className="text-xs font-bold uppercase tracking-wide" style={{ color: CHOCOLATE }}>
                    Working hours
                </Label>
                {!disabled && (
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className={`h-7 text-[11px] ${navBtn}`}
                        onClick={copyMondayToWeekdays}
                    >
                        Copy Mon → weekdays
                    </Button>
                )}
            </div>
            <div className="overflow-hidden rounded-lg border border-[#E8E4DE]">
                <div className="hidden border-b border-[#E8E4DE]/70 bg-[#FAF9F6] px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide text-[#8A8178] sm:grid sm:grid-cols-[120px_88px_88px_auto] sm:gap-2">
                    <span>Day</span>
                    <span>Open</span>
                    <span>Close</span>
                    <span />
                </div>
                {DAY_KEYS.map((day, i) => {
                    const row = hours[day] || DEFAULT_HOURS[day];
                    return (
                        <div
                            key={day}
                            className={`grid grid-cols-1 gap-2 px-3 py-2.5 sm:grid-cols-[120px_88px_88px_auto] sm:items-center ${
                                i < DAY_KEYS.length - 1 ? "border-b border-[#E8E4DE]/70" : ""
                            } ${row.closed ? "bg-[#FAF9F6]/80" : "bg-white"}`}
                        >
                            <span className="text-xs font-semibold" style={{ color: CHOCOLATE }}>
                                {DAY_LABELS[day]}
                            </span>
                            <div className="flex items-center gap-2 sm:contents">
                                <span className="text-[10px] font-bold uppercase text-[#8A8178] sm:hidden">Open</span>
                                <Input
                                    type="time"
                                    className={`h-8 text-xs ${fieldClass}`}
                                    value={row.open}
                                    disabled={disabled || row.closed}
                                    onChange={(e) => patchDay(day, { open: e.target.value })}
                                />
                            </div>
                            <div className="flex items-center gap-2 sm:contents">
                                <span className="text-[10px] font-bold uppercase text-[#8A8178] sm:hidden">Close</span>
                                <Input
                                    type="time"
                                    className={`h-8 text-xs ${fieldClass}`}
                                    value={row.close}
                                    disabled={disabled || row.closed}
                                    onChange={(e) => patchDay(day, { close: e.target.value })}
                                />
                            </div>
                            <label className="flex items-center gap-2 text-[11px] font-semibold text-[#8A8178]">
                                <Checkbox
                                    checked={!!row.closed}
                                    disabled={disabled}
                                    onCheckedChange={(checked) => patchDay(day, { closed: !!checked })}
                                    className="border-[#E8E4DE] data-[state=checked]:border-[#4A0E17] data-[state=checked]:bg-[#4A0E17]"
                                />
                                Closed
                            </label>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function SettingsSkeleton() {
    return (
        <div className="mx-auto max-w-3xl space-y-6 p-6 lg:p-8 animate-pulse" data-testid="settings-page">
            <div className="h-8 w-48 rounded bg-[#E8E4DE]/60" />
            <div className="h-4 w-64 rounded bg-[#E8E4DE]/40" />
            {[1, 2, 3].map((n) => (
                <div key={n} className="h-40 rounded-xl border border-[#E8E4DE] bg-white" />
            ))}
        </div>
    );
}

export default function Settings() {
    const { user, setUser } = useAuth();
    const [tenant, setTenant] = useState(null);
    const [tenantSaved, setTenantSaved] = useState(null);
    const [outlets, setOutlets] = useState([]);
    const [outletsSaved, setOutletsSaved] = useState([]);
    const [loading, setLoading] = useState(true);
    const [savingBiz, setSavingBiz] = useState(false);
    const [savingOutletId, setSavingOutletId] = useState(null);
    const [team, setTeam] = useState([]);
    const [roster, setRoster] = useState([]);
    const [invite, setInvite] = useState({
        email: "", full_name: "", role: "receptionist", mobile: "", staff_id: "",
    });
    const [inviting, setInviting] = useState(false);
    const [lastInviteLink, setLastInviteLink] = useState("");
    const [payroll, setPayroll] = useState(null);
    const [payrollSaved, setPayrollSaved] = useState(null);
    const [savingPayroll, setSavingPayroll] = useState(false);

    const isOwner = user?.role === "owner";
    const bookingUrl = buildBookingUrl(user?.tenant_slug);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [t, o, me] = await Promise.all([
                api.get("/tenant"),
                api.get("/outlets"),
                api.get("/auth/me"),
            ]);
            let payrollData = null;
            if (me.data.role === "owner") {
                try {
                    const pr = await api.get("/tenant/payroll");
                    payrollData = pr.data;
                } catch {
                    payrollData = { ...DEFAULT_PAYROLL };
                }
            }
            const normalizedOutlets = o.data.map(normalizeOutlet);
            let outletsList = normalizedOutlets;
            if (normalizedOutlets.length === 0 && me.data.role === "owner") {
                const { data: created } = await api.post("/outlets", {
                    name: `${t.data.name || "Salon"} – Main`,
                    address: "",
                    city: t.data.city || "",
                    phone: me.data.mobile || "",
                    gstin: "",
                    working_hours: DEFAULT_HOURS,
                });
                outletsList = [normalizeOutlet(created)];
            }
            setTenant(t.data);
            setTenantSaved(clone(t.data));
            setOutlets(outletsList);
            setOutletsSaved(clone(outletsList));
            setUser(me.data);
            if (payrollData) {
                setPayroll(payrollData);
                setPayrollSaved(clone(payrollData));
            } else if (me.data.role === "owner") {
                setPayroll({ ...DEFAULT_PAYROLL });
                setPayrollSaved(clone(DEFAULT_PAYROLL));
            }
        } catch {
            toast.error("Could not load settings");
        } finally {
            setLoading(false);
        }
    }, [setUser]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (!isOwner) return;
        (async () => {
            try {
                const [t, s] = await Promise.all([api.get("/team"), api.get("/staff")]);
                setTeam(t.data);
                setRoster(s.data);
            } catch {
                /* team list optional on load failure */
            }
        })();
    }, [isOwner]);

    const sendInvite = async () => {
        if (!invite.email.trim() || !invite.full_name.trim()) {
            toast.error("Email and name are required");
            return;
        }
        if (needsStaffProfile(invite.role) && !invite.staff_id) {
            toast.error(`Link a ${invite.role} roster profile for this login`);
            return;
        }
        setInviting(true);
        try {
            const payload = {
                email: invite.email.trim(),
                full_name: invite.full_name.trim(),
                role: invite.role,
                mobile: invite.mobile.trim(),
                staff_id: needsStaffProfile(invite.role) ? invite.staff_id : null,
            };
            const { data } = await api.post("/team/invite", payload);
            setTeam((prev) => [...prev, data.member]);
            setLastInviteLink(`${window.location.origin}${data.invite_path}`);
            setInvite({ email: "", full_name: "", role: "receptionist", mobile: "", staff_id: "" });
            toast.success("Invite created — share the activation link");
        } catch (err) {
            const d = err.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Could not send invite");
        } finally {
            setInviting(false);
        }
    };

    const copyInviteLink = () => {
        if (!lastInviteLink) return;
        navigator.clipboard.writeText(lastInviteLink);
        toast.success("Invite link copied");
    };

    const deactivateMember = async (memberId) => {
        try {
            await api.delete(`/team/${memberId}`);
            setTeam((prev) => prev.map((m) => (m.id === memberId ? { ...m, active: false } : m)));
            toast.success("Team member deactivated");
        } catch {
            toast.error("Could not deactivate member");
        }
    };

    const tenantDirty = useMemo(
        () => tenant && tenantSaved && JSON.stringify(tenant) !== JSON.stringify(tenantSaved),
        [tenant, tenantSaved],
    );

    const outletDirty = useCallback(
        (idx) => outlets[idx] && outletsSaved[idx]
            && JSON.stringify(outlets[idx]) !== JSON.stringify(outletsSaved[idx]),
        [outlets, outletsSaved],
    );

    const tenantErrors = useMemo(() => {
        if (!tenant) return {};
        const errs = {};
        if (!tenant.name?.trim()) errs.name = "Business name is required";
        return errs;
    }, [tenant]);

    const outletErrors = useCallback((o) => {
        const errs = {};
        const salonName = tenant?.name?.trim() || o.name?.trim();
        if (!salonName) errs.name = "Set salon name in Salon identity above";
        if (!validPhone(o.phone)) errs.phone = "Enter a valid phone (10–13 digits)";
        if (!validGstin(o.gstin)) errs.gstin = "Invalid GSTIN (15 chars, e.g. 27ABCDE1234F1Z5)";
        return errs;
    }, [tenant?.name]);

    const saveBusiness = async () => {
        if (!tenant || Object.keys(tenantErrors).length) return;
        setSavingBiz(true);
        try {
            const { data } = await api.put("/tenant", {
                name: tenant.name.trim(),
                city: (tenant.city || "").trim(),
                business_type: tenant.business_type || "salon",
            });
            setTenant(data);
            setTenantSaved(clone(data));
            const { data: me } = await api.get("/auth/me");
            setUser(me);
            const { data: refreshedOutlets } = await api.get("/outlets");
            const normalized = refreshedOutlets.map(normalizeOutlet);
            setOutlets(normalized);
            setOutletsSaved(clone(normalized));
            toast.success("Salon name updated everywhere");
        } catch (err) {
            const msg = err.response?.data?.detail;
            toast.error(typeof msg === "string" ? msg : "Failed to save");
        } finally {
            setSavingBiz(false);
        }
    };

    const discardBusiness = () => setTenant(clone(tenantSaved));

    const payrollDirty = useMemo(
        () => payroll && payrollSaved && JSON.stringify(payroll) !== JSON.stringify(payrollSaved),
        [payroll, payrollSaved],
    );

    const savePayroll = async () => {
        if (!payroll) return;
        setSavingPayroll(true);
        try {
            const { data } = await api.put("/tenant/payroll", payroll);
            setPayroll(data);
            setPayrollSaved(clone(data));
            toast.success("Payroll settings saved");
        } catch (err) {
            const msg = err.response?.data?.detail;
            toast.error(typeof msg === "string" ? msg : "Failed to save payroll settings");
        } finally {
            setSavingPayroll(false);
        }
    };

    const discardPayroll = () => setPayroll(clone(payrollSaved));

    const saveOutlet = async (o, idx) => {
        const errs = outletErrors(o);
        if (Object.keys(errs).length) {
            toast.error("Fix validation errors before saving");
            return;
        }
        setSavingOutletId(o.id);
        try {
            const { data } = await api.put(`/outlets/${o.id}`, {
                name: (tenant?.name || o.name).trim(),
                address: (o.address || "").trim(),
                city: (tenant?.city || o.city || "").trim(),
                phone: (o.phone || "").trim(),
                gstin: (o.gstin || "").trim().toUpperCase(),
                working_hours: o.working_hours || DEFAULT_HOURS,
            });
            const normalized = normalizeOutlet(data);
            setOutlets((prev) => prev.map((x, i) => (i === idx ? normalized : x)));
            setOutletsSaved((prev) => prev.map((x, i) => (i === idx ? clone(normalized) : x)));
            toast.success("Location saved");
        } catch (err) {
            const detail = err.response?.data?.detail;
            const msg = Array.isArray(detail)
                ? detail.map((d) => d.msg || d).join(", ")
                : typeof detail === "string"
                    ? detail
                    : "Failed to save location";
            toast.error(msg);
        } finally {
            setSavingOutletId(null);
        }
    };

    const discardOutlet = (idx) => {
        setOutlets((prev) => prev.map((o, i) => (i === idx ? clone(outletsSaved[idx]) : o)));
    };

    const locationIdx = primaryOutletIndex(outlets, user?.outlet_id);
    const location = locationIdx >= 0 ? outlets[locationIdx] : null;

    const patchTenant = (patch) => {
        setTenant((prev) => ({ ...prev, ...patch }));
        if (locationIdx < 0) return;
        const outletPatch = {};
        if ("name" in patch) outletPatch.name = patch.name;
        if ("city" in patch) outletPatch.city = patch.city;
        if (Object.keys(outletPatch).length) {
            setOutlet(locationIdx, outletPatch);
        }
    };

    const setOutlet = (idx, patch) => {
        setOutlets((prev) => prev.map((o, i) => (i === idx ? normalizeOutlet({ ...o, ...patch }) : o)));
    };

    const copyLink = () => {
        navigator.clipboard.writeText(bookingUrl);
        toast.success("Booking link copied");
    };

    if (loading) return <SettingsSkeleton />;

    return (
        <div className="mx-auto max-w-3xl space-y-6 p-6 lg:p-8" data-testid="settings-page">
            <header>
                <p className={EYEBROW} style={{ color: GOLD }}>Configuration</p>
                <h1 className="mt-1.5 text-[1.375rem] font-bold leading-snug sm:text-[1.5rem]" style={{ color: CHOCOLATE }}>
                    Settings
                </h1>
                <GoldDivider className="mt-3 max-w-[200px]" />
                <p className="mt-3 flex flex-wrap items-center gap-2 text-sm font-semibold text-[#8A8178]">
                    <MapPin className="size-4 text-[#D4AF37]" />
                    {tenant?.name || "Your salon"}
                    {user?.tenant_slug && (
                        <>
                            <span className="text-[#E8E4DE]">|</span>
                            <span className="font-mono text-xs">{user.tenant_slug}</span>
                        </>
                    )}
                </p>
            </header>

            {/* Business profile */}
            {tenant && (
                <SalonCard>
                    <div className="space-y-5 p-6">
                        <SectionHead
                            icon={Building2}
                            title="Salon identity"
                            subtitle="Your parlor name and city — used across the whole app"
                            iconBg="#FDF3DC"
                            iconColor={GOLD}
                        />
                        <div className="flex items-start gap-3 rounded-lg border border-[#E8E4DE]/80 bg-[#FAF9F6]/60 px-4 py-3 text-xs font-medium text-[#8A8178]">
                            <Receipt className="mt-0.5 size-4 shrink-0 text-[#D4AF37]" />
                            <span>
                                Changing the salon name updates the sidebar, top bar, public booking page,
                                receipts, and reports — all from this one field.
                            </span>
                        </div>
                        <div className="grid gap-4 md:grid-cols-2">
                            <FormField
                                label="Salon name"
                                error={tenantErrors.name}
                                hint="e.g. Demo Salon & Spa"
                                className="md:col-span-2"
                            >
                                <Input
                                    className={fieldClass}
                                    value={tenant.name || ""}
                                    onChange={(e) => patchTenant({ name: e.target.value })}
                                    disabled={!isOwner}
                                    data-testid="tenant-name"
                                />
                            </FormField>
                            <FormField label="City" hint="Shown on booking page and invoices">
                                <Input
                                    className={fieldClass}
                                    value={tenant.city || ""}
                                    onChange={(e) => patchTenant({ city: e.target.value })}
                                    disabled={!isOwner}
                                    placeholder="Mumbai"
                                />
                            </FormField>
                            <FormField label="Business type">
                                <Select
                                    value={tenant.business_type || "salon"}
                                    onValueChange={(v) => setTenant({ ...tenant, business_type: v })}
                                    disabled={!isOwner}
                                >
                                    <SelectTrigger className={fieldClass}>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="salon">Salon</SelectItem>
                                        <SelectItem value="spa">Spa</SelectItem>
                                        <SelectItem value="both">Salon & Spa</SelectItem>
                                    </SelectContent>
                                </Select>
                            </FormField>
                            <FormField
                                label="Booking slug"
                                hint="Used in your public booking URL — cannot be changed"
                                className="md:col-span-2"
                            >
                                <Input className={`${fieldClass} font-mono text-sm`} readOnly value={tenant.slug || ""} />
                            </FormField>
                        </div>
                        {isOwner ? (
                            <SaveBar
                                dirty={tenantDirty}
                                saving={savingBiz}
                                onSave={saveBusiness}
                                onDiscard={discardBusiness}
                                saveLabel="Save salon name"
                                disabled={Object.keys(tenantErrors).length > 0}
                            />
                        ) : (
                            <p className="flex items-center gap-2 text-xs font-medium text-[#8A8178]">
                                <Shield className="size-3.5" /> Only the owner can edit business settings.
                            </p>
                        )}
                    </div>
                </SalonCard>
            )}

            {/* Booking link */}
            <SalonCard>
                <div className="space-y-4 p-6">
                    <SectionHead
                        icon={Link2}
                        title="Public booking link"
                        subtitle="Share on Instagram, Google Business, or WhatsApp"
                        iconBg="#FDF3DC"
                        iconColor={GOLD}
                    />
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                        <Input
                            readOnly
                            value={bookingUrl}
                            className={`${fieldClass} font-medium`}
                            data-testid="booking-link-input"
                        />
                        <div className="flex shrink-0 gap-2">
                            <Button variant="outline" className={navBtn} onClick={copyLink} data-testid="copy-booking-link">
                                <Copy className="mr-1.5 size-4" /> Copy
                            </Button>
                            <a href={bookingUrl} target="_blank" rel="noreferrer">
                                <Button variant="outline" className={navBtn} aria-label="Open booking page">
                                    <ExternalLink className="size-4" />
                                </Button>
                            </a>
                        </div>
                    </div>
                </div>
            </SalonCard>

            {/* Payroll defaults */}
            {isOwner && payroll && (
                <SalonCard>
                    <div className="space-y-5 p-6">
                        <SectionHead
                            icon={Percent}
                            title="How your team gets paid"
                            subtitle="Choose once — the app shows only what matches your salon"
                            iconBg="#FDE8EC"
                            iconColor={MAROON}
                        />
                        {payrollDirty && (
                            <span className="inline-flex rounded-full bg-[#FDF3DC] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#9A7B10]">
                                Unsaved changes
                            </span>
                        )}

                        <div className="grid gap-3 sm:grid-cols-3">
                            {[
                                { id: "commission", title: "Commission based", desc: "Stylists earn % of their service sales" },
                                { id: "fixed", title: "Fixed salary", desc: "Monthly or weekly pay only — no commission" },
                                { id: "mixed", title: "Mixed team", desc: "Different pay per person (salary + commission)" },
                            ].map((opt) => {
                                const active = (payroll.salon_pay_model || "mixed") === opt.id;
                                return (
                                    <button
                                        key={opt.id}
                                        type="button"
                                        onClick={() => setPayroll((p) => ({ ...p, salon_pay_model: opt.id }))}
                                        className={`rounded-xl border p-4 text-left transition-all ${
                                            active
                                                ? "border-[#4A0E17] bg-[#FFF5F7] shadow-[0_4px_14px_rgba(74,14,23,0.08)]"
                                                : "border-[#E8E4DE] bg-white hover:border-[#D4AF37]/45"
                                        }`}
                                        data-testid={`pay-model-${opt.id}`}
                                    >
                                        <p className="font-serif text-sm font-bold" style={{ color: active ? MAROON : CHOCOLATE }}>
                                            {opt.title}
                                        </p>
                                        <p className="mt-1.5 text-xs font-medium leading-snug text-[#8A8178]">{opt.desc}</p>
                                    </button>
                                );
                            })}
                        </div>

                        <div className="grid gap-4 md:grid-cols-2">
                            <FormField label="Pay period" hint="For payroll reports">
                                <Select
                                    value={payroll.pay_period || "monthly"}
                                    onValueChange={(v) => setPayroll((p) => ({ ...p, pay_period: v }))}
                                >
                                    <SelectTrigger className={fieldClass} data-testid="payroll-period"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="weekly">Weekly</SelectItem>
                                        <SelectItem value="biweekly">Bi-weekly</SelectItem>
                                        <SelectItem value="monthly">Monthly</SelectItem>
                                    </SelectContent>
                                </Select>
                            </FormField>

                            {payroll.salon_pay_model === "fixed" && (
                                <FormField label="Default monthly salary (₹)" hint="Pre-filled when adding staff">
                                    <Input
                                        type="number"
                                        min={0}
                                        className={fieldClass}
                                        value={payroll.default_base_salary ?? 15000}
                                        onChange={(e) => setPayroll((p) => ({ ...p, default_base_salary: Number(e.target.value) }))}
                                        data-testid="payroll-default-salary"
                                    />
                                </FormField>
                            )}

                            {(payroll.salon_pay_model === "commission" || payroll.salon_pay_model === "mixed") && (
                                <FormField label="Default commission %" hint="Pre-filled for new stylists">
                                    <Input
                                        type="number"
                                        min={0}
                                        max={100}
                                        className={fieldClass}
                                        value={payroll.default_commission_pct ?? 40}
                                        onChange={(e) => setPayroll((p) => ({ ...p, default_commission_pct: Number(e.target.value) }))}
                                        data-testid="payroll-default-commission"
                                    />
                                </FormField>
                            )}

                            {(payroll.salon_pay_model === "commission" || payroll.salon_pay_model === "mixed") && (
                                <>
                                    <FormField label="Commission basis" className="md:col-span-2">
                                        <Select
                                            value={payroll.commission_basis || "pre_discount"}
                                            onValueChange={(v) => setPayroll((p) => ({ ...p, commission_basis: v }))}
                                        >
                                            <SelectTrigger className={fieldClass}><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="pre_discount">Before invoice discount</SelectItem>
                                                <SelectItem value="post_discount">After invoice discount</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </FormField>
                                    <FormField label="Product commission" className="md:col-span-2">
                                        <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-[#E8E4DE] bg-[#FFFEF9] px-4 py-3">
                                            <Checkbox
                                                checked={!!payroll.commission_on_products}
                                                onCheckedChange={(v) => setPayroll((p) => ({ ...p, commission_on_products: !!v }))}
                                            />
                                            <span className="text-sm font-medium text-[#5C534C]">Include retail / product sales in commission</span>
                                        </label>
                                    </FormField>
                                </>
                            )}
                        </div>
                        <SaveBar
                            dirty={payrollDirty}
                            saving={savingPayroll}
                            onSave={savePayroll}
                            onDiscard={discardPayroll}
                            saveLabel="Save pay settings"
                        />
                    </div>
                </SalonCard>
            )}

            {/* Location & billing (single salon) */}
            <SalonCard>
                <div className="space-y-5 p-6">
                    <SectionHead
                        icon={MapPin}
                        title="Location & billing"
                        subtitle="Address, GSTIN, and hours — shown on receipts and reports"
                        iconBg="#E0EDFA"
                        iconColor="#3B82C4"
                    />
                    <div className="flex items-start gap-3 rounded-lg border border-[#E8E4DE]/80 bg-[#FAF9F6]/60 px-4 py-3 text-xs font-medium text-[#8A8178]">
                        <Receipt className="mt-0.5 size-4 shrink-0 text-[#D4AF37]" />
                        <span>
                            Keep phone, address, and GSTIN accurate for GST invoices and printed revenue reports.
                        </span>
                    </div>

                    {!location ? (
                        <p className="text-sm font-medium text-[#8A8178]">
                            {isOwner
                                ? "Loading location details…"
                                : "Ask the owner to set up salon location and billing details."}
                        </p>
                    ) : (() => {
                        const i = locationIdx;
                        const o = location;
                        const errs = outletErrors(o);
                        const dirty = outletDirty(i);
                        return (
                            <>
                                {dirty && (
                                    <span className="inline-flex rounded-full bg-[#FDF3DC] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#9A7B10]">
                                        Unsaved changes
                                    </span>
                                )}
                                <div className="grid gap-4 md:grid-cols-2">
                                    <FormField label="Phone" error={errs.phone} className="md:col-span-2 sm:col-span-1">
                                        <div className="relative">
                                            <Phone className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[#8A8178]" />
                                            <Input
                                                className={`pl-9 ${fieldClass}`}
                                                value={o.phone || ""}
                                                onChange={(e) => setOutlet(i, { phone: e.target.value })}
                                                disabled={!isOwner}
                                                placeholder="+91 98765 43210"
                                                data-testid="location-phone"
                                            />
                                        </div>
                                    </FormField>
                                    <FormField label="Address" className="md:col-span-2">
                                        <Textarea
                                            className={`min-h-[72px] resize-y ${fieldClass}`}
                                            value={o.address || ""}
                                            onChange={(e) => setOutlet(i, { address: e.target.value })}
                                            disabled={!isOwner}
                                            placeholder="Street, area, landmark"
                                            rows={2}
                                            data-testid="location-address"
                                        />
                                    </FormField>
                                    <FormField label="GSTIN" error={errs.gstin} hint="15-character GST number (optional)" className="md:col-span-2 sm:col-span-1">
                                        <Input
                                            className={`${fieldClass} font-mono uppercase`}
                                            value={o.gstin || ""}
                                            onChange={(e) => setOutlet(i, { gstin: e.target.value.toUpperCase() })}
                                            disabled={!isOwner}
                                            placeholder="27ABCDE1234F1Z5"
                                            maxLength={15}
                                            data-testid="location-gstin"
                                        />
                                    </FormField>
                                </div>

                                <WorkingHoursEditor
                                    value={o.working_hours}
                                    onChange={(wh) => setOutlet(i, { working_hours: wh })}
                                    disabled={!isOwner}
                                />

                                {isOwner ? (
                                    <SaveBar
                                        dirty={dirty}
                                        saving={savingOutletId === o.id}
                                        onSave={() => saveOutlet(o, i)}
                                        onDiscard={() => discardOutlet(i)}
                                        saveLabel="Save location"
                                        disabled={Object.keys(errs).length > 0}
                                    />
                                ) : (
                                    <p className="flex items-center gap-2 text-xs font-medium text-[#8A8178]">
                                        <Shield className="size-3.5" /> Only the owner can edit location and billing.
                                    </p>
                                )}
                            </>
                        );
                    })()}
                </div>
            </SalonCard>

            {isOwner && (
                <SalonCard>
                    <div className="space-y-5 p-6">
                        <SectionHead
                            icon={Users}
                            title="Team access"
                            subtitle="Invite managers, receptionists, stylists, and therapists — each gets their own login"
                            iconBg="#F3E8FF"
                            iconColor="#7C3AED"
                        />
                        <div className="flex items-start gap-3 rounded-lg border border-[#E8E4DE]/80 bg-[#FAF9F6]/60 px-4 py-3 text-xs font-medium text-[#8A8178]">
                            <Mail className="mt-0.5 size-4 shrink-0 text-[#D4AF37]" />
                            <span>
                                Logins for front desk and service providers. Add roster profiles on{" "}
                                <Link to="/app/staff" className="font-bold underline" style={{ color: MAROON }}>Staff</Link>{" "}
                                first, then invite a <strong className="text-[#5C534C]">Stylist</strong> or{" "}
                                <strong className="text-[#5C534C]">Therapist</strong> login and link it to that profile.
                                Calendar access: own appointments only.
                            </span>
                        </div>

                        <div className="grid gap-4 md:grid-cols-2">
                            <FormField label="Full name">
                                <Input
                                    className={fieldClass}
                                    value={invite.full_name}
                                    onChange={(e) => setInvite((p) => ({ ...p, full_name: e.target.value }))}
                                    placeholder="Priya Sharma"
                                    data-testid="team-invite-name"
                                />
                            </FormField>
                            <FormField label="Email">
                                <Input
                                    type="email"
                                    className={fieldClass}
                                    value={invite.email}
                                    onChange={(e) => setInvite((p) => ({ ...p, email: e.target.value }))}
                                    placeholder="priya@salon.com"
                                    data-testid="team-invite-email"
                                />
                            </FormField>
                            <FormField label="Role">
                                <Select
                                    value={invite.role}
                                    onValueChange={(v) => setInvite((p) => ({ ...p, role: v, staff_id: "" }))}
                                >
                                    <SelectTrigger className={fieldClass} data-testid="team-invite-role">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="manager">Manager</SelectItem>
                                        <SelectItem value="receptionist">Receptionist</SelectItem>
                                        <SelectItem value="stylist">Stylist</SelectItem>
                                        <SelectItem value="therapist">Therapist</SelectItem>
                                    </SelectContent>
                                </Select>
                            </FormField>
                            <FormField label="Mobile" hint="Optional">
                                <Input
                                    className={fieldClass}
                                    value={invite.mobile}
                                    onChange={(e) => setInvite((p) => ({ ...p, mobile: e.target.value }))}
                                    placeholder="+91 98765 43210"
                                />
                            </FormField>
                            {needsStaffProfile(invite.role) && (
                                <FormField
                                    label="Staff profile"
                                    hint={`Link login to a ${invite.role} on your roster`}
                                    className="md:col-span-2"
                                >
                                    <Select
                                        value={invite.staff_id || ""}
                                        onValueChange={(v) => setInvite((p) => ({ ...p, staff_id: v }))}
                                    >
                                        <SelectTrigger className={fieldClass} data-testid="team-invite-staff">
                                            <SelectValue placeholder={`Select ${invite.role}`} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {roster
                                                .filter((s) => s.role === rosterRoleForLogin(invite.role))
                                                .map((s) => (
                                                <SelectItem key={s.id} value={s.id}>
                                                    {s.full_name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormField>
                            )}
                        </div>

                        <Button
                            type="button"
                            className={primaryBtn}
                            disabled={inviting}
                            onClick={sendInvite}
                            data-testid="team-invite-submit"
                        >
                            <UserPlus className="mr-1.5 size-4" />
                            {inviting ? "Creating invite…" : "Create invite link"}
                        </Button>

                        {lastInviteLink && (
                            <div className="flex flex-col gap-2 rounded-lg border border-[#D4AF37]/35 bg-[#FDF3DC]/45 p-3 sm:flex-row sm:items-center">
                                <code className="min-w-0 flex-1 truncate text-xs text-[#3D2817]">{lastInviteLink}</code>
                                <Button type="button" variant="outline" size="sm" className={navBtn} onClick={copyInviteLink}>
                                    <Copy className="mr-1.5 size-3.5" /> Copy link
                                </Button>
                            </div>
                        )}

                        {team.length > 0 && (
                            <div className="overflow-hidden rounded-lg border border-[#E8E4DE]">
                                <div className="border-b border-[#E8E4DE]/70 bg-[#FAF9F6] px-4 py-2 text-[10px] font-bold uppercase tracking-wide text-[#8A8178]">
                                    Team members
                                </div>
                                <ul className="divide-y divide-[#E8E4DE]/70">
                                    {team.map((m) => (
                                        <li key={m.id} className="flex flex-wrap items-center gap-2 px-4 py-3 text-sm">
                                            <div className="min-w-0 flex-1">
                                                <p className="font-semibold text-[#2D2926]">{m.full_name}</p>
                                                <p className="truncate text-xs text-[#8A8178]">{m.email}</p>
                                            </div>
                                            <span className="rounded-full bg-[#F3E8FF] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#7C3AED]">
                                                {ROLE_LABELS[m.role] || m.role}
                                            </span>
                                            {!m.active && (
                                                <span className="rounded-full bg-[#FEF5F3] px-2.5 py-0.5 text-[10px] font-bold uppercase text-[#C84B31]">
                                                    {m.invited ? "Pending invite" : "Inactive"}
                                                </span>
                                            )}
                                            {m.role !== "owner" && m.active && (
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    className={`h-7 text-xs ${navBtn}`}
                                                    onClick={() => deactivateMember(m.id)}
                                                    data-testid={`team-deactivate-${m.id}`}
                                                >
                                                    Deactivate
                                                </Button>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </SalonCard>
            )}

            {/* Payments */}
            <SalonCard>
                <div className="space-y-3 p-6">
                    <SectionHead
                        icon={CreditCard}
                        title="Payments"
                        subtitle="How clients pay at checkout"
                        iconBg="#EDE8F5"
                        iconColor="#7C3AED"
                    />
                    <ul className="space-y-2.5 text-sm font-medium text-[#8A8178]">
                        <li className="flex gap-2">
                            <span className="text-[#D4AF37]">·</span>
                            <span>
                                <strong style={{ color: CHOCOLATE }}>Cash / UPI / Card</strong>
                                {" "}— mark paid manually at POS (no setup needed).
                            </span>
                        </li>
                        <li className="flex gap-2">
                            <span className="text-[#D4AF37]">·</span>
                            <span>
                                <strong style={{ color: CHOCOLATE }}>Razorpay</strong>
                                {" "}— optional online payments; configure API keys on the server.
                            </span>
                        </li>
                    </ul>
                </div>
            </SalonCard>
        </div>
    );
}
